// Cuenta del local: cuanto se le mando y que movimientos registro
// administracion. Solo lectura — los pagos y devoluciones los carga la
// confiteria desde su pantalla de Cuentas.

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { abrirDetalleLocal } from "./detallelocal";
import { alLlegarAviso } from "../notificaciones";
import { nombreTurno } from "../../lib/types";
import type { CuentaDeLocal, TipoMov, LocalDeUsuario, Sesion, Turno } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

const ETIQUETA: Record<TipoMov, string> = {
  COMPRA: "Pedido",
  PAGO: "Pago",
  DEVOLUCION: "Devolución",
  FALTANTE: "Faltante",
  ANULACION: "Pedido anulado",
};

export async function renderClienteCuenta(contenedor: HTMLElement): Promise<void> {
  // La cuenta es POR LOCAL: si la persona maneja varios, elige cual mirar.
  let locales: LocalDeUsuario[] = [];
  try {
    locales = (await api.get<Sesion>("/api/sesion")).locales ?? [];
  } catch {
    /* sin sesion no deberia llegar aca */
  }
  const clienteId = Number(sessionStorage.getItem("cliente_local_elegido")) || locales[0]?.id || 0;

  const selector =
    locales.length > 1
      ? `<label class="tarjeta mb-4 flex items-center justify-between gap-3 py-3">
          <span class="etiqueta mb-0">Local</span>
          <select id="local" class="campo max-w-[14rem]">
            ${locales
              .map(
                (l) =>
                  `<option value="${l.id}"${l.id === clienteId ? " selected" : ""}>${esc(l.nombre)}</option>`,
              )
              .join("")}
          </select>
        </label>`
      : "";

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Mi cuenta</h1>
      <p class="subtitulo">Resumen de lo enviado y lo registrado por la confitería.</p>
    </div>
    ${selector}
    <div id="cuerpo"><div class="tarjeta text-center text-masa-400">Cargando...</div></div>`;

  const cuerpo = contenedor.querySelector("#cuerpo") as HTMLElement;
  const selLocal = contenedor.querySelector("#local") as HTMLSelectElement | null;
  selLocal?.addEventListener("change", () => {
    sessionStorage.setItem("cliente_local_elegido", selLocal.value);
    void renderClienteCuenta(contenedor);
  });

  let datos: CuentaDeLocal;
  try {
    datos = await api.get<CuentaDeLocal>(`/api/cliente/cuenta?cliente_id=${clienteId}`);
  } catch (e) {
    cuerpo.innerHTML = `<div class="tarjeta text-red-700">${esc(
      e instanceof Error ? e.message : "Error",
    )}</div>`;
    return;
  }

  // Las compras se pueden tocar para ver que se pidio ese dia. El turno va
  // debajo del tipo y no en columna aparte: con dos pedidos el mismo dia es lo
  // unico que los distingue, y en el celular no entra otra columna.
  const filas = datos.movimientos
    .map((m) => {
      const tocable = m.pedido_id !== null;
      return `
      <tr ${tocable ? `data-ver="${m.pedido_id}" data-turno="${esc(m.turno ?? "")}" data-entrega="${esc(m.fecha_entrega ?? "")}" class="cursor-pointer hover:bg-crema-50 active:bg-crema-100"` : ""}>
        <td>${esc(m.fecha)}</td>
        <td>
          ${ETIQUETA[m.tipo] ?? esc(m.tipo)}${m.pedido_id ? ` <span class="text-masa-400">#${esc(m.pedido_id)}</span>` : ""}
          ${m.turno ? `<span class="block text-xs text-masa-400">${esc(nombreTurno(m.turno))}${tocable ? " · toca para ver el detalle" : ""}</span>` : ""}
        </td>
        <td class="text-right tabular-nums ${m.tipo === "COMPRA" ? "" : "text-green-700"}">${
          m.tipo === "COMPRA" ? "" : "− "
        }${formatearPesos(m.monto)}</td>
      </tr>`;
    })
    .join("");

  cuerpo.innerHTML = `
    <div class="tarjeta mb-4 text-center">
      <div class="text-xs uppercase tracking-wider text-masa-400">Saldo</div>
      <div class="text-3xl font-bold tabular-nums">${formatearPesos(datos.saldo)}</div>
    </div>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr><th>Fecha</th><th>Movimiento</th><th class="text-right">Monto</th></tr></thead>
        <tbody>${
          filas || `<tr><td colspan="3" class="py-6 text-center text-masa-400">Sin movimientos.</td></tr>`
        }</tbody>
      </table>
    </div>
    <dialog id="dlg"></dialog>`;

  // Al confirmarse un pedido se genera la COMPRA: con el push, el saldo se
  // actualiza solo en vez de quedar viejo hasta la proxima visita.
  alLlegarAviso(cuerpo, () => void renderClienteCuenta(contenedor));

  const dlg = cuerpo.querySelector("#dlg") as HTMLDialogElement;
  cuerpo.addEventListener("click", (ev) => {
    const fila = (ev.target as HTMLElement).closest<HTMLElement>("[data-ver]");
    if (!fila) return;
    void abrirDetalleLocal(dlg, Number(fila.dataset.ver), {
      fecha_entrega: fila.dataset.entrega || null,
      turno: (fila.dataset.turno as Turno) || null,
    });
  });
}
